import React from "react";
import Joi from "joi-browser";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { Form, Modal, Button, Spinner } from "react-bootstrap";
import { getTariffs, addTariff, changePublishedStatus } from "../services/tariffService";
import { getRouters } from "../services/routerService";
import FormComponent from "./common/formComponent";
import Published from "./common/published";
import { formatNumber } from "../services/util";
import PageLoading from "../components/common/pageLoading";
import FloatingAddButton from "./common/floatingAddButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { isSuperAdmin, isEngineer } from "../services/authService";
import { StyledTableCell, StyledTableRow } from "../services/tableStyles";

class Tariffs extends FormComponent {
  state = {
    data: {
      title: "",
      type: "",
      category: "",
      price: "",
      validity: "",
      isPublished: "false",
      isDecommisioned: "false",
      pppProfile: "",
      nRouterId: "",
    },
    errors: {},
    tariffs: [],
    routers: [],
    showAddModal: false,
    showConfirmModal: false,
    loading: "",
    processing: false,
  };

  constructor() {
    super();
    this.state.loading = true;
  }

  schema = {
    title: Joi.string().required().label("Title"),
    type: Joi.string().required().label("Type"),
    category: Joi.string().required().label("Category"),
    price: Joi.number().min(0).required().label("Price"),
    validity: Joi.number().min(1).required().label("Validity"),
    isPublished: Joi.boolean().required().label("Published"),
    isDecommisioned: Joi.boolean().required().label("Decommisioned"),
    pppProfile: Joi.string().required().label("PPP Profile"),
    nRouterId: Joi.string().required().label("Router"),
  };

  doSubmit = async () => {
    this.setState({ processing: true });
    const { data: payload, tariffs: originalTariffs } = this.state;

    try {
      const { data: newTariff } = await addTariff(payload);

      const tariffs = [...originalTariffs];
      tariffs.unshift(newTariff);
      this.setState({ tariffs, showAddModal: false });
      toast.success("Tariff Added");
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
    this.setState({ processing: false });
  };

  showConfirmModal = (oltToDelete) => {
    this.setState({ showConfirmModal: true, oltToDelete });
  };

  handleShowAddModal = () => {
    this.setState({ showAddModal: true });
  };

  handleClose = () => {
    this.setState({ showAddModal: false, showConfirmModal: false });
  };

  async populateTariffs() {
    try {
      const { data: tariffs } = await getTariffs();
      this.setState({ tariffs });
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast(ex.response.data);
      }
    }
    this.setState({ loading: false });
  }

  async populateRouters() {
    try {
      const { data: routers } = await getRouters();
      this.setState({ routers });
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast(ex.response.data);
      }
    }
    this.setState({ loading: false });
  }

  async componentDidMount() {
    await this.populateTariffs();
    await this.populateRouters();
  }

  handleChangePublishedStatus = async (tariff) => {
    const { tariffs: originalTariffs } = this.state;

    const tariffs = [...originalTariffs];
    const index = tariffs.indexOf(tariff);
    tariff.isPublished = !tariff.isPublished;
    tariffs[index] = tariff;

    this.setState({ tariffs });
    toast.success("Published Status Changed");

    try {
      await changePublishedStatus(tariff._id, tariff.isPublished);
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        this.setState({ tariffs: originalTariffs });
        toast(ex.response.data);
      }
    }
  };

  render() {
    const { data, errors, tariffs, routers, showAddModal, loading, processing } = this.state;

    return (
      <Paper elevation={3}>
        <div className="card">
          <div className="card-header main-color-bg">
            <div>
              <h5>Tariff Plans</h5>
            </div>
          </div>
          <div className="card-body">
            <span className="card-text">
              {loading && <PageLoading loading={loading} />}
              {!loading && tariffs.length === 0 && <span>No Tariffs to Display</span>}
              {!loading && (isSuperAdmin() || isEngineer()) && (
                <FloatingAddButton classes="float-right mt-2 mb-3" onClick={this.handleShowAddModal} />
              )}
              {!loading && tariffs.length > 0 && (
                <TableContainer component={Paper}>
                  <Table aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Title</StyledTableCell>
                        <StyledTableCell>Price</StyledTableCell>
                        <StyledTableCell>Router</StyledTableCell>
                        <StyledTableCell>Type</StyledTableCell>
                        <StyledTableCell>Category</StyledTableCell>
                        <StyledTableCell>Validity</StyledTableCell>
                        {(isSuperAdmin() || isEngineer()) && <StyledTableCell>Published</StyledTableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tariffs.map((tariff) => (
                        <StyledTableRow key={tariff._id}>
                          {(isEngineer() || isSuperAdmin()) && (
                            <StyledTableCell>
                              <Link to={`/tariffs/${tariff._id}`}>{tariff.title}</Link>
                            </StyledTableCell>
                          )}
                          {!isEngineer() && !isSuperAdmin() && <StyledTableCell>{tariff.title}</StyledTableCell>}
                          <StyledTableCell>&#8358;{formatNumber(tariff.price)}</StyledTableCell>
                          <StyledTableCell>{tariff.nRouterName}</StyledTableCell>
                          <StyledTableCell>{tariff.type}</StyledTableCell>
                          <StyledTableCell>{tariff.category}</StyledTableCell>
                          <StyledTableCell>{tariff.validity} Day(s)</StyledTableCell>
                          {(isSuperAdmin() || isEngineer()) && (
                            <StyledTableCell>
                              <Published
                                status={tariff.isPublished}
                                onClick={() => this.handleChangePublishedStatus(tariff)}
                              />
                            </StyledTableCell>
                          )}
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </span>
          </div>
        </div>

        <Modal show={showAddModal} size="lg" onHide={this.handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Add Tariff</Modal.Title>
          </Modal.Header>
          <Form onSubmit={this.handleSubmit}>
            <Modal.Body>
              <Form.Group controlId="title">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  name="title"
                  onChange={this.handleChange}
                  value={data.title}
                  size="sm"
                  type="text"
                  autoFocus
                />
                {errors.title && <span classtitle="text-danger">{errors.title}</span>}
              </Form.Group>

              <Form.Group controlId="type">
                <Form.Label>Type</Form.Label>
                <Form.Control
                  as="select"
                  custom
                  name="type"
                  onChange={this.handleChange}
                  value={data.type}
                  size="sm"
                  type="text"
                >
                  <option value="">Select type</option>
                  <option value="Internet">Internet</option>
                  <option value="TV">TV</option>
                  <option value="Voice">Voice</option>
                </Form.Control>
                {errors.type && <span className="text-danger">{errors.type}</span>}
              </Form.Group>

              <Form.Group controlId="category">
                <Form.Label>Category</Form.Label>
                <Form.Control
                  as="select"
                  custom
                  name="category"
                  onChange={this.handleChange}
                  value={data.category}
                  size="sm"
                  type="text"
                >
                  <option value="">Select Category</option>
                  <option value="Shared">Shared</option>
                  <option value="Dedicated">Dedicated</option>
                </Form.Control>
                {errors.category && <span className="text-danger">{errors.category}</span>}
              </Form.Group>

              <Form.Group controlId="price">
                <Form.Label>Price</Form.Label>
                <Form.Control name="price" onChange={this.handleChange} value={data.price} size="sm" type="text" />
                {errors.price && <span className="text-danger">{errors.price}</span>}
              </Form.Group>

              <Form.Group controlId="vat">
                <Form.Label>PPP Profile</Form.Label>
                <Form.Control
                  name="pppProfile"
                  onChange={this.handleChange}
                  value={data.pppProfile}
                  size="sm"
                  type="text"
                />
                {errors.pppProfile && <span className="text-danger">{errors.pppProfile}</span>}
              </Form.Group>

              <Form.Group controlId="validity">
                <Form.Label>
                  Validity <small>(Days)</small>
                </Form.Label>

                <Form.Control
                  name="validity"
                  onChange={this.handleChange}
                  value={data.validity}
                  size="sm"
                  type="text"
                />
                {errors.validity && <span className="text-danger">{errors.validity}</span>}
              </Form.Group>

              <Form.Group controlId="nRouterId">
                <Form.Label>Router</Form.Label>
                <Form.Control
                  as="select"
                  custom
                  name="nRouterId"
                  onChange={this.handleChange}
                  value={data.nRouterId}
                  size="sm"
                  type="text"
                >
                  <option value="">Select Router</option>
                  {routers.map((router) => (
                    <option key={router._id} value={router._id}>
                      {router.name} ({router.host})
                    </option>
                  ))}
                </Form.Control>
                {errors.nRouterId && <span className="text-danger">{errors.nRouterId}</span>}
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button onClick={this.handleClose} variant="secondary">
                Close
              </Button>
              <Button disabled={this.validate() || processing} type="submit" variant="primary">
                {processing && (
                  <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
                )}
                Add
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Paper>
    );
  }
}

export default Tariffs;
